import React from 'react'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import {
  useTrackPurchaseFlow,
  useChargebeeCheckoutMutation,
  beforeRequestHook
} from '@mote/api'
import { WarningIcon } from '@mote/assets/icons'
import NON_IDEAL_STATE_SVG from '@mote/ui/components/nonIdealState/non-ideal-state.svg'
import { OnboardingLayout } from '../../layouts'
import { MessageCard } from '../Onboarding/components'
import { useAuth } from '../../auth/useAuth'
import ky from 'ky'
import { API_BASE_URL } from '@mote/config/constants'
const queryString = require('query-string')

export const apiClient = ky.extend({
  prefixUrl: API_BASE_URL,
  hooks: {
    beforeRequest: [beforeRequestHook]
  }
})

export const CheckoutUnlimitedScreen = () => {
  const history = useHistory()
  const { user } = useAuth()
  const [state, setState] = React.useState({
    checking: true,
    loadingPortal: false,
    needsLogin: false,
    alreadyPaid: false,
    error: null
  })
  const { mutate: trackPurchaseFlow } = useTrackPurchaseFlow()
  const { mutate: createChargebeeCheckout } = useChargebeeCheckoutMutation()
  const messageCardProps = React.useMemo(() => {
    if (state.checking || state.loadingPortal) {
      return {
        title: 'Hold on',
        message: state.loadingPortal
          ? 'Connecting you to payment gateway...'
          : 'Checking you are logged in first...',
        loading: true,
        showTextOnLoading: true
      }
    }

    if (state.needsLogin) {
      return {
        title: 'Oh dear...',
        message:
          "Seems you're not logged in. Please sign in to mote in order to proceed!",
        icon: (
          <WarningIcon width={60} height={60} style={{ margin: '-12px' }} />
        ),
        ctaProps: {
          text: "Let's do it",
          onClick: () => {
            history.push(buildLoginURL())
          }
        }
      }
    }

    if (state.alreadyPaid) {
      return {
        title: 'License already activated',
        message: (
          <>
            Seems you have already purchased an Unlimited License. There is no
            need to purchase again. If you think a mistake was made and you
            haven't made a payment, please kindly contact{' '}
            <Link href='mailto:support@mote.com'>support@mote.com</Link> to
            assist you on what to do next.
          </>
        ),
        icon: (
          <WarningIcon width={60} height={60} style={{ margin: '-12px' }} />
        ),
        ctaProps: {
          text: 'Return to home',
          onClick: () => {
            history.push('/')
          }
        }
      }
    }

    if (state.error) {
      return {
        title: 'Oh no!',
        message: state.error,
        icon: <img src={NON_IDEAL_STATE_SVG} alt='Error occurred' />
      }
    }

    return {
      title: 'Oh no!',
      message: (
        <>
          An unknown error occurred. If the problem persists kindly contact{' '}
          <Link href='mailto:support@mote.com'>support@mote.com</Link>
        </>
      ),
      icon: <img src={NON_IDEAL_STATE_SVG} alt='Error occurred' />
    }
  }, [state])

  function buildLoginURL(qs) {
    const returnTo = `/checkout/unlimited${window.location.search}`

    return `/login?force=1&inPurchaseFlow=1&returnIfAdult=1&returnTo=${encodeURIComponent(
      returnTo
    )}`
  }

  React.useEffect(() => {
    trackPurchaseFlow({ productEnum: 'unlimited', step: 4, stage: 'Loading' })

    async function checkAccountStatus() {
      const qs = queryString.parse(window.location.search)
      if (!(qs.quantity && qs.productId)) {
        setState(prev => ({
          ...prev,
          checking: false,
          error: "Couldn't find quantity and productId"
        }))
        trackPurchaseFlow({
          productEnum: 'unlimited',
          step: 4,
          stage: 'No quantity or productId',
          hasFailed: true
        })
      } else if (user?.isPaid) {
        setState(prev => ({
          ...prev,
          checking: false,
          alreadyPaid: true
        }))
        trackPurchaseFlow({
          productEnum: 'unlimited',
          step: 4,
          stage: 'Already has an unlimited license',
          hasFailed: true
        })
      } else {
        setState(prev => ({
          ...prev,
          checking: false,
          loadingPortal: true
        }))

        const institutionMode = qs.quantity > 1
        let institutionData = {}
        if (institutionMode) {
          institutionData.institutionName = ''
          institutionData.institutionRegion = ''
          institutionData.institutionCountryCode = ''
        }

        const response = await createChargebeeCheckout({
          variables: {
            priceId: qs.productId,
            quantity: qs.quantity,
            institutionMode,
            ...institutionData
          }
        })

        if (!response || response.error || !response.data) {
          setState(prev => ({
            ...prev,
            loadingPortal: false,
            error: 'Something bad happened on our end, try again later'
          }))
          trackPurchaseFlow({
            productEnum: 'unlimited',
            step: 5,
            stage: 'Cannot redirect to CB',
            data: {
              quantity: qs.quantity
            },
            hasFailed: true
          })
          return
        }

        await trackPurchaseFlow({
          productEnum: 'unlimited',
          step: 5,
          stage: 'Redirecting to CB',
          date: {
            quantity: qs.quantity
          }
        })

        window.location.href = response.data.URL
      }
    }

    apiClient
      .post('v1/i/signIn/AccessToken')
      .then(_ => {
        checkAccountStatus()
      })
      .catch(error => {
        if (error && error.response.status === 403) {
          setState(prev => ({
            ...prev,
            checking: false,
            needsLogin: true
          }))
          trackPurchaseFlow({
            productEnum: 'unlimited',
            step: 5,
            stage: 'Needs Login'
          })
        }
      })
  }, [])

  return (
    <OnboardingLayout skipGreeting>
      <MessageCard {...messageCardProps} />
    </OnboardingLayout>
  )
}

const Link = styled.a`
  color: #ac0ae8;
  font-size: 15px;
  font-weight: 500;

  &:hover,
  &:focus,
  &:visited {
    color: #ac0ae8;
  }

  &:hover {
    text-decoration: none;
  }
`
